import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, AlertTriangle, TrendingUp } from 'lucide-react';
import { segments, segmentOrder, segmentLabels } from '@/config/segments';

const DEFAULT_TITLE = 'CostCraft';
const DEFAULT_DESCRIPTION = 'CostCraft - Recipe costing for Australian bakers';

export const SegmentPage = () => {
  const { segment } = useParams();
  const navigate = useNavigate();
  const data = segments[segment];

  useEffect(() => {
    if (!data) return;
    document.title = data.title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', data.metaDescription);
    return () => {
      document.title = DEFAULT_TITLE;
      if (meta) meta.setAttribute('content', DEFAULT_DESCRIPTION);
    };
  }, [data]);

  if (!data) {
    navigate('/', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F8F9FC]" data-testid="segment-page">
      {/* Top Nav */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#DBEAFE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/costcraft-logo.jpg" alt="CostCraft" className="w-9 h-9 rounded-md object-contain" />
            <span className="text-xl sm:text-2xl font-bold text-[#2C5F7C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              CostCraft
            </span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={() => navigate('/auth')}
              className="text-sm font-medium text-[#2C5F7C] hover:underline"
              data-testid="segment-nav-signin-btn"
            >
              Sign In
            </button>
            <Button
              onClick={() => navigate('/auth?mode=signup')}
              className="rounded-full bg-[#2C5F7C] hover:bg-[#1E4A60] text-white text-sm px-4 sm:px-5"
              data-testid="segment-nav-start-free-btn"
            >
              Start Free
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-10 text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#EFF6FF] text-[#2C5F7C] text-xs font-medium px-3 py-1 mb-6">
          <TrendingUp className="w-3.5 h-3.5" />
          Recipe costing for {segmentLabels[segment].toLowerCase()}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#111827] leading-[1.05] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="segment-headline">
          {data.headline}
        </h1>
        <p className="text-base sm:text-lg text-[#6B7280] leading-relaxed mb-8 max-w-2xl mx-auto" data-testid="segment-subheadline">
          {data.subheadline}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={() => navigate('/auth?mode=signup')}
            className="rounded-full bg-[#2C5F7C] hover:bg-[#1E4A60] text-white h-12 px-8 text-base"
            data-testid="segment-start-free-btn"
          >
            Start Free
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            onClick={() => navigate('/#pricing')}
            variant="outline"
            className="rounded-full border-[#2C5F7C] text-[#2C5F7C] hover:bg-[#EFF6FF] h-12 px-8 text-base"
            data-testid="segment-pricing-btn"
          >
            See pricing
          </Button>
        </div>
        <p className="text-sm text-[#6B7280] mt-5 font-medium" data-testid="segment-trust-line">
          Made in Australia · GST-aware · Cancel anytime
        </p>
      </section>

      {/* Pain points */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-14">
        <div className="grid sm:grid-cols-3 gap-5">
          {data.painPoints.map((point, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-[#DBEAFE] p-6"
              data-testid={`segment-pain-point-${i}`}
            >
              <AlertTriangle className="w-5 h-5 text-[#9B7EBD] mb-3" />
              <p className="text-sm text-[#374151] leading-relaxed">{point}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Worked example */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        <div className="bg-white rounded-2xl border border-[#DBEAFE] overflow-hidden" data-testid="segment-example">
          <div className="bg-[#2C5F7C] px-6 sm:px-8 py-4">
            <span className="text-xs font-bold tracking-wide text-white/80 uppercase">{data.example.label}</span>
            <h3 className="text-lg sm:text-xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {data.example.title}
            </h3>
          </div>
          <div className="px-6 sm:px-8 py-6">
            {data.example.lines.map((line, i) => (
              <div
                key={i}
                className={`flex items-center justify-between py-2.5 ${i !== data.example.lines.length - 1 ? 'border-b border-[#F3F4F6]' : ''}`}
                data-testid={`segment-example-line-${i}`}
              >
                <span className={`text-sm sm:text-base ${line.bold ? 'font-semibold text-[#111827]' : 'text-[#6B7280]'}`}>
                  {line.name}
                </span>
                <span className={`text-sm sm:text-base ${line.bold ? 'font-bold text-[#2C5F7C]' : 'text-[#374151]'}`}>
                  {line.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#2C5F7C]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Know your real numbers today
          </h2>
          <p className="text-white/85 text-base sm:text-lg mb-8">
            Start free in minutes. Upgrade only when you're ready.
          </p>
          <Button
            onClick={() => navigate('/auth?mode=signup')}
            className="rounded-full bg-white text-[#2C5F7C] hover:bg-[#EFF6FF] h-12 px-8 text-base"
            data-testid="segment-footer-start-free-btn"
          >
            Start Free
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F8F9FC] border-t border-[#DBEAFE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#6B7280]">
          <div className="flex items-center gap-2">
            <img src="/costcraft-logo.jpg" alt="CostCraft" className="w-7 h-7 rounded-md object-contain" />
            <span className="font-semibold text-[#2C5F7C]" style={{ fontFamily: 'Outfit, sans-serif' }}>CostCraft</span>
          </div>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            {segmentOrder.filter((s) => s !== segment).map((s) => (
              <Link key={s} to={`/for/${s}`} className="hover:text-[#2C5F7C]" data-testid={`segment-footer-link-${s}`}>
                {segmentLabels[s]}
              </Link>
            ))}
            <button onClick={() => navigate('/terms')} className="hover:text-[#2C5F7C]">Terms & Privacy</button>
          </div>
          <span className="text-[#9CA3AF]">© 2026 CostCraft. Prices in AUD.</span>
        </div>
      </footer>
    </div>
  );
};

export default SegmentPage;
